<template>
  <div class="mcal">
    <div class="mcal__head">
      <button type="button" class="mcal__nav" @click="shift(-1)">
        <v-icon small>{{ $vuetify.rtl ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
      </button>
      <button type="button" class="mcal__title" @click="goToday">{{ title }}</button>
      <button type="button" class="mcal__nav" @click="shift(1)">
        <v-icon small>{{ $vuetify.rtl ? 'mdi-chevron-left' : 'mdi-chevron-right' }}</v-icon>
      </button>
    </div>
    <div class="mcal__week">
      <span v-for="(n, i) in weekdays" :key="i">{{ n }}</span>
    </div>
    <div class="mcal__grid" @touchstart.passive="touchStart" @touchend.passive="touchEnd">
      <template v-for="week in weeks">
        <button
          v-for="d in week"
          :key="d.key"
          type="button"
          class="mcal__day"
          :class="{
            'mcal__day--out': !d.inMonth,
            'mcal__day--today': d.isToday,
            'mcal__day--sel': d.key === selected,
            'mcal__day--we': d.weekend,
          }"
          @click="selected = d.key"
        >
          <span class="mcal__num">{{ d.label }}</span>
          <span class="mcal__dots">
            <i
              v-for="t in (byDay[d.key] || []).slice(0, 3)"
              :key="t.key"
              :style="{ background: t.col.color || '#90a4ae' }"
            />
          </span>
        </button>
      </template>
    </div>

    <div class="mcal__agenda">
      <div class="mcal__agenda-title">
        {{ selectedTitle }}
        <span class="mcal__agenda-count">{{ dayTasks.length }}</span>
      </div>
      <div v-if="!dayTasks.length" class="mcal__empty">{{ $t('taskDeskEmpty') }}</div>
      <task-card
        v-for="t in dayTasks"
        :key="t.key"
        class="mcal__card"
        :el="t.el"
        :color="t.col.color"
        :done="t.done"
        mobile
        @more="$emit('more', t)"
      />
    </div>
  </div>
</template>

<script>
import TaskCard from './TaskCard'
import { tasksByDay } from './CalendarView'
import { today, dayKey, monthGrid, weekdayNames, monthTitle, addMonths, fmt } from '../deskDates'
import moment from 'jalali-moment'

export default {
  name: 'MobileCalendar',
  components: { TaskCard },
  inject: ['desk'],
  data () {
    return { cursor: today(), selected: dayKey(today()), tx: 0, ty: 0 }
  },
  computed: {
    weeks () {
      return monthGrid(this.cursor, this.desk.jalali)
    },
    weekdays () {
      return weekdayNames(this.desk.jalali)
    },
    title () {
      return monthTitle(this.cursor, this.desk.jalali)
    },
    byDay () {
      return tasksByDay(this.desk.tasks)
    },
    dayTasks () {
      return this.byDay[this.selected] || []
    },
    selectedTitle () {
      return fmt(moment.from(this.selected, 'en', 'YYYY-MM-DD'), 'dddd D MMMM', this.desk.jalali)
    },
  },
  methods: {
    shift (n) {
      this.cursor = addMonths(this.cursor, n, this.desk.jalali)
    },
    goToday () {
      this.cursor = today()
      this.selected = dayKey(today())
    },
    touchStart (e) {
      this.tx = e.changedTouches[0].clientX
      this.ty = e.changedTouches[0].clientY
    },
    touchEnd (e) {
      const dx = e.changedTouches[0].clientX - this.tx
      const dy = e.changedTouches[0].clientY - this.ty
      if (Math.abs(dx) < 60 || Math.abs(dy) > 50) return
      const forward = this.$vuetify.rtl ? dx > 0 : dx < 0
      this.shift(forward ? 1 : -1)
    },
  },
}
</script>

<style scoped>
.mcal {
  padding: 0 12px 96px;
}
.mcal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
}
.mcal__nav {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #e6e9ef;
}
.theme--dark .mcal__nav {
  background: #1e1e1e;
  border-color: #333;
}
.mcal__title {
  font-weight: 700;
  font-size: 0.95rem;
}
.mcal__week,
.mcal__grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}
.mcal__week span {
  text-align: center;
  font-size: 0.72rem;
  color: #8a94a6;
  padding: 4px 0;
}
.mcal__grid {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e6e9ef;
  padding: 4px;
  row-gap: 2px;
}
.theme--dark .mcal__grid {
  background: #1e1e1e;
  border-color: #333;
}
.mcal__day {
  height: 46px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  border-radius: 8px;
}
.mcal__num {
  width: 28px;
  height: 28px;
  line-height: 28px;
  border-radius: 14px;
  text-align: center;
  font-size: 0.85rem;
}
.mcal__day--we .mcal__num {
  color: #c2185b;
}
.mcal__day--out .mcal__num {
  color: #c0c6cf;
}
.mcal__day--today .mcal__num {
  color: var(--v-primary-base);
  font-weight: 700;
  box-shadow: inset 0 0 0 1.5px var(--v-primary-base);
}
.mcal__day--sel .mcal__num {
  background: var(--v-primary-base);
  color: #fff;
  box-shadow: none;
}
.mcal__dots {
  display: flex;
  gap: 2px;
  height: 5px;
}
.mcal__dots i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
}
.mcal__agenda-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 0.9rem;
  margin: 16px 2px 10px;
}
.mcal__agenda-count {
  font-size: 0.72rem;
  font-weight: 400;
  padding: 0 7px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.06);
}
.mcal__empty {
  text-align: center;
  color: #9aa4b2;
  font-size: 0.85rem;
  padding: 24px 0;
}
.mcal__card + .mcal__card {
  margin-top: 8px;
}
</style>
